/** A router for eight routes.
 *
 *  React Router would do this too, and bring a good deal more than eight
 *  routes' worth of code with it. This is the whole thing: a path, a listener
 *  on popstate, and a Link that does not reload the page. */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

const RouteContext = createContext<string>('/');

/** The route is the path and the query together.
 *
 *  Tracking only the pathname looked like an economy until an expired sign-in
 *  link: the recovery button navigates from /signin?token=… to /signin, the
 *  pathname does not change, nothing rerenders, and the person sits on the
 *  dead-link screen until they reload. */
const here = () => window.location.pathname + window.location.search;

export function Router({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(here);

  useEffect(() => {
    const onPop = () => setPath(here());
    window.addEventListener('popstate', onPop);
    window.addEventListener('omibee:navigate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('omibee:navigate', onPop);
    };
  }, []);

  return <RouteContext.Provider value={path}>{children}</RouteContext.Provider>;
}

/** usePath is the pathname alone, which is what the screens match on. */
export function usePath(): string {
  const route = useContext(RouteContext);
  const q = route.indexOf('?');
  return q === -1 ? route : route.slice(0, q);
}

/** useQuery is the query string, for the few places that read one. */
export function useQuery(): URLSearchParams {
  const route = useContext(RouteContext);
  const q = route.indexOf('?');
  return new URLSearchParams(q === -1 ? '' : route.slice(q));
}

export function navigate(to: string, replace = false) {
  if (replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  window.dispatchEvent(new Event('omibee:navigate'));
  window.scrollTo(0, 0);
}

export function Link(
  { to, children, ...rest }: { to: string; children: ReactNode } & React.HTMLAttributes<HTMLAnchorElement>,
) {
  const onClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Leave the modified clicks alone: a middle click or a cmd-click should
      // open a tab, as it does anywhere else.
      if (event.defaultPrevented || event.button !== 0 || event.metaKey ||
          event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      navigate(to);
    },
    [to],
  );
  return <a href={to} onClick={onClick} {...rest}>{children}</a>;
}

/** match pulls the parameters out of a pattern like "/projects/:id". */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const actual = path.split('/').filter(Boolean);
  if (p.length !== actual.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const segment = p[i]!;
    const value = actual[i]!;
    if (segment.startsWith(':')) params[segment.slice(1)] = decodeURIComponent(value);
    else if (segment !== value) return null;
  }
  return params;
}

/** Where to go after signing in: the path somebody was on their way to, if it
 *  is a path on this site, and their own page otherwise. The server drops a
 *  foreign next before it goes into the mail; this is the same rule again,
 *  for a next typed into the address bar. */
export function afterSignIn(next: string | null): string {
  if (
    !next || next.length > 512 || !next.startsWith('/') || next.startsWith('//') ||
    next.startsWith('/signin') ||
    [...next].some((c) => c === '\\' || c.charCodeAt(0) < 0x20 || c.charCodeAt(0) === 0x7f)
  ) {
    return '/profile';
  }
  return next;
}
